import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import AnnoSelector from "@/components/AnnoSelector";
import AlunniSendBar from "@/components/AlunniSendBar";
import useAlunniSelection from "@/lib/useAlunniSelection";

/**
 * Pagina "Moduli" (matrice alunni × categorie modulo).
 * Ogni cella = bottone:
 *   - colore BLANDO (grigio) → nessun documento per quella categoria
 *   - colore MARCATO (verde) → documento presente
 * Click su bottone → menu inline con: Carica / Visualizza / Scarica / Elimina.
 *
 * Richiesta utente 2026-02-16.
 */
const fmtItDate = (yyyymmdd) => {
  if (!yyyymmdd) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(yyyymmdd);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : yyyymmdd;
};

export default function ModuliAlunniPage() {
  const { annoCorrente } = useAuth();
  const { gestione } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ anno: null, categorie: [], items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [filterSet, setFilterSet] = useState("");
  const sel = useAlunniSelection();
  const [filterCla, setFilterCla] = useState("");
  const [sortMode, setSortMode] = useState("settore_classe"); // "settore_classe" | "cognome"
  const [filtroAttivi, setFiltroAttivi] = useState(""); // "" (tutti) | "attivi" (con almeno un dato)
  const [activeCell, setActiveCell] = useState(null);   // {cod, cat_id}
  const fileInputRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [notaDrafts, setNotaDrafts] = useState({});     // key `${cod}|${cat_id}` → testo nota in editing
  const [validoDalDrafts, setValidoDalDrafts] = useState({});
  const [validoFinoDrafts, setValidoFinoDrafts] = useState({});
  const [showStampaModal, setShowStampaModal] = useState(false);
  const [stampaCatId, setStampaCatId] = useState("");

  const notaKey = (cod, cat_id) => `${cod}|${cat_id}`;
  const getNotaDraft = (cod, cat_id, fallback = "") => {
    const k = notaKey(cod, cat_id);
    return notaDrafts[k] !== undefined ? notaDrafts[k] : fallback;
  };
  const setNotaDraft = (cod, cat_id, val) => {
    setNotaDrafts(d => ({ ...d, [notaKey(cod, cat_id)]: val }));
  };
  const getValidoDal = (cod, cat_id, fallback = "") => {
    const k = notaKey(cod, cat_id);
    return validoDalDrafts[k] !== undefined ? validoDalDrafts[k] : fallback;
  };
  const setValidoDal = (cod, cat_id, val) => {
    setValidoDalDrafts(d => ({ ...d, [notaKey(cod, cat_id)]: val }));
  };
  const getValidoFino = (cod, cat_id, fallback = "") => {
    const k = notaKey(cod, cat_id);
    return validoFinoDrafts[k] !== undefined ? validoFinoDrafts[k] : fallback;
  };
  const setValidoFino = (cod, cat_id, val) => {
    setValidoFinoDrafts(d => ({ ...d, [notaKey(cod, cat_id)]: val }));
  };
  /** Calcola lo stato di validità in base alle 2 date (oggi vs intervallo). */
  const computeValidity = (dal, fino) => {
    if (!dal && !fino) return { state: "none", label: "" };
    const today = new Date().toISOString().slice(0, 10);
    if (dal && today < dal) return { state: "futuro", label: `In attesa: valido dal ${fmtItDate(dal)}` };
    if (fino && today > fino) return { state: "scaduto", label: `⚠ SCADUTO il ${fmtItDate(fino)}` };
    return { state: "ok", label: `In corso${fino ? " · fino al " + fmtItDate(fino) : ""}` };
  };

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const params = annoCorrente ? { anno: annoCorrente } : {};
      const r = await api.get("/documenti-alunni/matrice", { params });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [annoCorrente]);

  const setoriList = useMemo(() => {
    const s = new Set(data.items.map(it => it.settore).filter(Boolean));
    return Array.from(s).sort();
  }, [data.items]);
  const classiList = useMemo(() => {
    const s = new Set(data.items.filter(it => !filterSet || it.settore === filterSet).map(it => it.classe).filter(Boolean));
    return Array.from(s).sort();
  }, [data.items, filterSet]);

  // Richiesta utente 2026-06: la pagina Idoneità Sportiva mostra SOLO i moduli sportivi
  // (idoneità/esonero sport), non tutte le categorie del catalogo documenti.
  const categorieSport = useMemo(
    () => data.categorie.filter(c => /sport|idoneit|esoner/i.test(`${c.codice || ""} ${c.descrizione || ""}`)),
    [data.categorie]
  );

  // Flag di riga: ha dati / ha almeno un'idoneità in corso / ha almeno una scaduta.
  const rowFlags = (it) => {
    let hasData = false, hasActive = false, hasExpired = false;
    for (const c of categorieSport) {
      const d = it.documenti?.[c.id];
      if (!d) continue;
      if (d.numero_protocollo || d.filename) hasData = true;
      const st = computeValidity(d.protocollo_valido_dal || "", d.protocollo_valido_fino_al || d.protocollo_scadenza || "").state;
      if (st === "ok") hasActive = true;
      if (st === "scaduto") hasExpired = true;
    }
    return { hasData, hasActive, hasExpired };
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = data.items.filter(it => {
      if (filterSet && it.settore !== filterSet) return false;
      if (filterCla && it.classe !== filterCla) return false;
      if (q && !(`${it.Cog} ${it.Nom}`.toLowerCase().includes(q) || String(it.Cod).includes(q))) return false;
      if (filtroAttivi) {
        const f = rowFlags(it);
        if (filtroAttivi === "con_dati" && !f.hasData) return false;
        if (filtroAttivi === "attivi" && !f.hasActive) return false;
        if (filtroAttivi === "scaduti" && !f.hasExpired) return false;
      }
      return true;
    });
    const cmpStr = (a, b) => (a || "").localeCompare(b || "", "it", { sensitivity: "base" });
    if (sortMode === "cognome") {
      return [...list].sort((a, b) => cmpStr(a.Cog, b.Cog) || cmpStr(a.Nom, b.Nom));
    }
    // default: settore + classe + cognome + nome
    return [...list].sort((a, b) =>
      cmpStr(a.settore, b.settore) ||
      cmpStr(a.classe, b.classe) ||
      cmpStr(a.Cog, b.Cog) ||
      cmpStr(a.Nom, b.Nom)
    );
  }, [data.items, search, filterSet, filterCla, sortMode, filtroAttivi, categorieSport]);

  const onUploadClick = (cod, cat_id) => {
    setUploadTarget({ cod, cat_id });
    setActiveCell(null);
    setTimeout(() => fileInputRef.current?.click(), 0);
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !uploadTarget) return;
    const { cod, cat_id } = uploadTarget;
    setUploadTarget(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/documenti-alunni/alunno/${cod}/categoria/${cat_id}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params: annoCorrente ? { anno: annoCorrente } : {},
      });
      await load();
    } catch (e2) {
      alert("Errore upload: " + (e2.response?.data?.detail || e2.message));
    }
  };

  const onView = (cod, cat_id) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const annoQ = annoCorrente ? `&anno=${annoCorrente}` : "";
    window.open(`${base}/api/documenti-alunni/alunno/${cod}/categoria/${cat_id}/file?inline=true${annoQ}`, "_blank", "noopener,noreferrer");
    setActiveCell(null);
  };
  const onDownload = (cod, cat_id) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const annoQ = annoCorrente ? `&anno=${annoCorrente}` : "";
    const a = document.createElement("a");
    a.href = `${base}/api/documenti-alunni/alunno/${cod}/categoria/${cat_id}/file?inline=false${annoQ}`;
    a.rel = "noopener noreferrer";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setActiveCell(null);
  };
  const onDelete = async (cod, cat_id) => {
    if (!window.confirm("Eliminare il documento?")) return;
    try {
      await api.delete(`/documenti-alunni/alunno/${cod}/categoria/${cat_id}`, {
        params: annoCorrente ? { anno: annoCorrente } : {},
      });
      await load();
      setActiveCell(null);
    } catch (e2) {
      alert("Errore: " + (e2.response?.data?.detail || e2.message));
    }
  };

  const onRegistraProtocollo = async (cod, cat_id) => {
    try {
      const nota = getNotaDraft(cod, cat_id, "");
      const valido_dal = getValidoDal(cod, cat_id, "");
      const valido_fino_al = getValidoFino(cod, cat_id, "");
      const r = await api.post(`/documenti-alunni/alunno/${cod}/categoria/${cat_id}/protocollo`,
        { nota, valido_dal, valido_fino_al },
        { params: annoCorrente ? { anno: annoCorrente } : {} });
      setData(d => ({
        ...d,
        items: d.items.map(it => it.Cod === cod ? {
          ...it,
          documenti: {
            ...it.documenti,
            [cat_id]: { ...(it.documenti[cat_id] || {}),
              numero_protocollo: r.data.numero_protocollo,
              protocollo_at: r.data.protocollo_at,
              protocollo_sigla: r.data.sigla,
              protocollo_nota: r.data.nota || "",
              protocollo_valido_dal: r.data.valido_dal || "",
              protocollo_valido_fino_al: r.data.valido_fino_al || "" },
          },
        } : it),
      }));
      // pulisci draft
      setNotaDrafts(d => { const x = { ...d }; delete x[notaKey(cod, cat_id)]; return x; });
      setValidoDalDrafts(d => { const x = { ...d }; delete x[notaKey(cod, cat_id)]; return x; });
      setValidoFinoDrafts(d => { const x = { ...d }; delete x[notaKey(cod, cat_id)]; return x; });
      setActiveCell(null);
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  const onSalvaNota = async (cod, cat_id) => {
    // Aggiorna nota + date di validità di un protocollo già esistente.
    try {
      const nota = getNotaDraft(cod, cat_id, "");
      const valido_dal = getValidoDal(cod, cat_id, "");
      const valido_fino_al = getValidoFino(cod, cat_id, "");
      const r = await api.post(`/documenti-alunni/alunno/${cod}/categoria/${cat_id}/protocollo`,
        { nota, valido_dal, valido_fino_al },
        { params: annoCorrente ? { anno: annoCorrente } : {} });
      setData(d => ({
        ...d,
        items: d.items.map(it => it.Cod === cod ? {
          ...it,
          documenti: {
            ...it.documenti,
            [cat_id]: { ...(it.documenti[cat_id] || {}),
              protocollo_nota: r.data.nota || "",
              protocollo_valido_dal: r.data.valido_dal || "",
              protocollo_valido_fino_al: r.data.valido_fino_al || "" },
          },
        } : it),
      }));
      setNotaDrafts(d => { const x = { ...d }; delete x[notaKey(cod, cat_id)]; return x; });
      setValidoDalDrafts(d => { const x = { ...d }; delete x[notaKey(cod, cat_id)]; return x; });
      setValidoFinoDrafts(d => { const x = { ...d }; delete x[notaKey(cod, cat_id)]; return x; });
      setActiveCell(null);
    } catch (e) {
      alert("Errore salvataggio: " + (e.response?.data?.detail || e.message));
    }
  };

  const onPulisciCampo = async (cod, cat_id) => {
    if (!window.confirm("Pulire completamente il campo? Verrà cancellato il file (se presente), il protocollo e la nota. L'operazione NON è reversibile.")) return;
    try {
      // 1) Cancella file se presente (e con esso anche tutto il record documenti_alunni)
      const it = data.items.find(x => x.Cod === cod);
      const doc = it?.documenti?.[cat_id];
      if (doc?.filename) {
        await api.delete(`/documenti-alunni/alunno/${cod}/categoria/${cat_id}`,
          { params: annoCorrente ? { anno: annoCorrente } : {} });
      }
      // 2) Reset protocollo (rimuove anche eventuali campi protocollo residui se non c'era file)
      await api.delete(`/documenti-alunni/alunno/${cod}/categoria/${cat_id}/protocollo`,
        { params: annoCorrente ? { anno: annoCorrente } : {} });
      // 3) Pulisci eventuale draft nota locale
      setNotaDrafts(d => { const x = { ...d }; delete x[notaKey(cod, cat_id)]; return x; });
      await load();
      setActiveCell(null);
    } catch (e) {
      alert("Errore pulizia: " + (e.response?.data?.detail || e.message));
    }
  };

  const onDuplicaAnnoSuccessivo = async (cod, cat_id) => {
    if (!annoCorrente) { alert("Anno scolastico corrente non disponibile"); return; }
    const annoNext = annoCorrente + 1;
    const label = `${annoNext}/${((annoNext + 1) % 100).toString().padStart(2, "0")}`;
    if (!window.confirm(`Duplicare lo stesso protocollo per l'A.S. ${label}?\n\nMantiene numero, date di validità e nota.`)) return;
    try {
      const r = await api.post(
        `/documenti-alunni/alunno/${cod}/categoria/${cat_id}/duplica-anno-successivo`,
        {},
        { params: annoCorrente ? { anno: annoCorrente } : {} },
      );
      if (r.data?.already_exists) {
        alert(`Esiste già un protocollo ${r.data.numero_protocollo} per l'A.S. ${label}. Non sovrascritto.`);
      } else {
        alert(`✓ Protocollo ${r.data.numero_protocollo} duplicato per l'A.S. ${label}.\n\nValido dal ${r.data.valido_dal || "—"} al ${r.data.valido_fino_al || "—"}.`);
      }
      setActiveCell(null);
    } catch (e) {
      alert("Errore duplicazione: " + (e.response?.data?.detail || e.message));
    }
  };

  const onStampaCategoria = () => {
    if (!stampaCatId) { alert("Seleziona un modulo da stampare"); return; }
    const base = process.env.REACT_APP_BACKEND_URL;
    const annoQ = annoCorrente ? `?anno=${annoCorrente}` : "";
    window.open(`${base}/api/documenti-alunni/stampa-categoria/${stampaCatId}${annoQ}`, "_blank", "noopener,noreferrer");
    setShowStampaModal(false);
  };

  const onResetProtocollo = async (cod, cat_id) => {
    if (!window.confirm("Resettare il protocollo? Verrà cancellato il numero (e il documento se non c'è un file caricato).")) return;
    try {
      await api.delete(`/documenti-alunni/alunno/${cod}/categoria/${cat_id}/protocollo`, {
        params: annoCorrente ? { anno: annoCorrente } : {},
      });
      await load();
      setActiveCell(null);
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  const fmtItDateTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch (e) { return iso; }
  };

  return (
    <div data-testid="moduli-alunni-page" style={{ padding: "12px 20px" }}>
      <div className="sw-page-title">
        <h1>🏅 Idoneità Sportiva</h1>
        <div className="sw-breadcrumb">Home &gt; Idoneità Sportiva</div>
        <button onClick={() => setShowStampaModal(true)}
          data-testid="moduli-stampa-btn"
          title="Stampa elenco alunni con protocollo attivo per un modulo"
          style={{ marginLeft: "auto", padding: "7px 14px", background: "#1565c0", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
          🖨 Stampa elenco
        </button>
        <button onClick={() => navigate(`/g/${gestione}/documenti_catalogo`)}
          data-testid="moduli-catalogo-btn"
          title="Gestisci le categorie di moduli (catalogo)"
          style={{ marginLeft: 8, padding: "7px 14px", background: "#5d4037", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
          📂 Catalogo Moduli
        </button>
      </div>

      <div className="sw-card">
        <div className="sw-card-body">
          <div className="sw-filter-row" style={{ flexWrap: "wrap", gap: 8 }}>
            <AnnoSelector testId="moduli-anno-attivo" />
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Cerca:</span>
            <input data-testid="moduli-search" type="text" placeholder="cognome, nome, cod..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="sw-filter-input" style={{ width: 200 }} />
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Settore:</span>
            <select className="sw-filter-select" value={filterSet} onChange={e => { setFilterSet(e.target.value); setFilterCla(""); }} style={{ minWidth: 100 }}>
              <option value="">Tutti</option>
              {setoriList.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Classe:</span>
            <select className="sw-filter-select" value={filterCla} onChange={e => setFilterCla(e.target.value)} style={{ minWidth: 100 }}>
              <option value="">Tutte</option>
              {classiList.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Mostra:</span>
            <select data-testid="moduli-filter-attivi" value={filtroAttivi} onChange={e => setFiltroAttivi(e.target.value)}
              title="Filtra per stato: con dati, idoneità attive (in corso) o scadute"
              className="sw-filter-select"
              style={{ minWidth: 150, background: filtroAttivi ? "#fff59d" : "white", fontWeight: filtroAttivi ? 700 : 400 }}>
              <option value="">Tutti</option>
              <option value="con_dati">Solo con dati inseriti</option>
              <option value="attivi">✅ Solo attivi (in corso)</option>
              <option value="scaduti">⚠ Solo scaduti</option>
            </select>
            {(search || filterSet || filterCla || filtroAttivi) && <button className="sw-filter-btn" onClick={() => { setSearch(""); setFilterSet(""); setFilterCla(""); setFiltroAttivi(""); }}>Pulisci</button>}
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Ordina:</span>
            <select className="sw-filter-select" value={sortMode} onChange={e => setSortMode(e.target.value)}
              data-testid="moduli-sort-select" style={{ minWidth: 180 }}>
              <option value="settore_classe">Settore · Classe · Cognome Nome</option>
              <option value="cognome">Cognome · Nome</option>
            </select>
            <span style={{ marginLeft: "auto", fontSize: 11, color: "#444" }}>
              <b>{filtered.length}</b> alunni · <b>{categorieSport.length}</b> moduli
            </span>
          </div>
        </div>
      </div>

      {err && <div style={{ padding: 10, background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a", margin: "8px 0", fontSize: 12 }}>{err}</div>}

      <div className="sw-card" style={{ marginTop: 8 }}>
        <div style={activeCell ? { overflow: "visible" } : { overflow: "auto", maxHeight: "70vh" }}>
          <table className="sw-table" style={{ minWidth: 600 + categorieSport.length * 115 }}>
            <thead className="thead-blue">
              <tr>
                <th style={{ width: 30, textAlign: "center" }}>
                  <input type="checkbox" data-testid="moduli-sel-all"
                    checked={filtered.length > 0 && filtered.every(it => sel.isSelected(it.Cod))}
                    onChange={e => {
                      if (e.target.checked) sel.selectAll(filtered.map(it => it.Cod));
                      else sel.clear();
                    }}
                    title="Seleziona/deseleziona tutti gli alunni visibili" />
                </th>
                <th style={{ width: 40, textAlign: "center" }}>#</th>
                <th style={{ width: 60, textAlign: "center" }}>Cod</th>
                <th style={{ minWidth: 160 }}>Cognome Nome</th>
                <th style={{ width: 60, textAlign: "center" }}>Set.</th>
                <th style={{ width: 60, textAlign: "center" }}>Cla.</th>
                {categorieSport.map(c => (
                  <th key={c.id} style={{ minWidth: 115, textAlign: "center" }} title={c.descrizione}>
                    <div style={{ fontSize: 10, fontWeight: 700, lineHeight: 1.2 }}>{c.codice || c.descrizione}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={7 + categorieSport.length} className="sw-loading">Caricamento…</td></tr>
                : filtered.length === 0 ? <tr><td colSpan={7 + categorieSport.length} className="sw-empty"><p>Nessun alunno trovato</p></td></tr>
                  : filtered.map((it, i) => {
                    const rf = rowFlags(it);
                    return (
                    <tr key={it.Cod} data-testid={`moduli-row-${it.Cod}`}
                        style={{ background: sel.isSelected(it.Cod) ? "#f3e5f5" : (rf.hasExpired ? "#ffebee" : undefined) }}>
                      <td style={{ textAlign: "center", borderLeft: rf.hasExpired ? "3px solid #c62828" : undefined }}>
                        <input type="checkbox" data-testid={`moduli-sel-${it.Cod}`}
                          checked={sel.isSelected(it.Cod)} onChange={() => sel.toggle(it.Cod)} />
                      </td>
                      <td style={{ color: "#888", textAlign: "center" }}>{i + 1}</td>
                      <td style={{ textAlign: "center", fontFamily: "Consolas" }}>{it.Cod}</td>
                      <td style={{ fontWeight: 600 }}>{it.Cog} {it.Nom}</td>
                      <td style={{ textAlign: "center" }}>{it.settore || "-"}</td>
                      <td style={{ textAlign: "center", fontWeight: 600 }}>{it.classe || "-"}</td>
                      {categorieSport.map(c => {
                        const doc = it.documenti[c.id];
                        const hasProtocollo = !!doc?.numero_protocollo;
                        const hasFile = !!doc?.filename;
                        const has = hasProtocollo || hasFile;
                        const valDal = doc?.protocollo_valido_dal || "";
                        const valFino = doc?.protocollo_valido_fino_al || doc?.protocollo_scadenza || "";
                        const validity = computeValidity(valDal, valFino);
                        const scaduto = validity.state === "scaduto";
                        const futuro = validity.state === "futuro";
                        const isActive = activeCell && activeCell.cod === it.Cod && activeCell.cat_id === c.id;
                        const bgColor = scaduto ? "#c62828" : (futuro ? "#ef6c00" : (has ? "#2e7d32" : "#eceff1"));
                        const borderColor = scaduto ? "#7f0000" : (futuro ? "#bf360c" : (has ? "#1b5e20" : "#cfd8dc"));
                        return (
                          <td key={c.id} style={{ textAlign: "center", padding: 2, position: "relative" }}>
                            <button
                              onClick={() => setActiveCell(isActive ? null : { cod: it.Cod, cat_id: c.id })}
                              data-testid={`moduli-cell-${it.Cod}-${c.id}`}
                              title={
                                hasProtocollo
                                  ? `Protocollo ${doc.numero_protocollo} del ${fmtItDateTime(doc.protocollo_at)}${validity.label ? ` · ${validity.label}` : ""}`
                                  : (hasFile ? `File: ${doc.filename}` : "Nessun protocollo — clicca per registrare")
                              }
                              style={{
                                width: 108, padding: "4px 6px", borderRadius: 2,
                                background: bgColor,
                                color: has ? "white" : "#90a4ae",
                                border: `1px solid ${borderColor}`,
                                fontSize: 10, fontWeight: has ? 800 : 600, cursor: "pointer",
                                letterSpacing: 0.2, lineHeight: 1.2,
                                boxShadow: has ? "0 1px 2px rgba(0,0,0,0.2)" : "none",
                              }}>
                              {hasProtocollo ? (
                                <span style={{ display: "block" }}>
                                  <span style={{ fontFamily: "Consolas, monospace" }}>
                                    {scaduto ? "⚠ " : ""}{doc.numero_protocollo}{(doc.protocollo_nota || "").trim() ? " 📝" : ""}
                                  </span>
                                  {valFino && (
                                    <span style={{ display: "block", fontSize: 9, fontWeight: 700, opacity: 0.95, marginTop: 1 }}>
                                      🗓 {fmtItDate(valFino)}
                                    </span>
                                  )}
                                </span>
                              ) : hasFile ? "📄 FILE" : "＋ REGISTRA"}
                            </button>
                            {isActive && (
                              <div style={{
                                position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)",
                                background: "white", border: "1px solid #1a237e", borderRadius: 3, padding: 10,
                                display: "flex", flexDirection: "column", gap: 6, zIndex: 5, boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
                                minWidth: 260, marginTop: 2,
                              }} onClick={(e) => e.stopPropagation()}>
                                <div style={{ fontSize: 10, fontWeight: 700, color: "#0d47a1", borderBottom: "1px solid #e0e0e0", paddingBottom: 4, textAlign: "left" }}>
                                  {c.descrizione || c.codice} — {it.Cog} {it.Nom}
                                </div>
                                {hasProtocollo && (
                                  <div style={{ fontSize: 10, color: "#555", textAlign: "left", lineHeight: 1.4 }}>
                                    <b style={{ fontFamily: "Consolas, monospace", color: "#1b5e20" }}>{doc.numero_protocollo}</b>
                                    {" · "}{fmtItDateTime(doc.protocollo_at)}
                                  </div>
                                )}
                                {validity.label && (
                                  <div data-testid={`moduli-valid-state-${it.Cod}-${c.id}`}
                                    style={{
                                      padding: "4px 8px", borderRadius: 3, fontSize: 11, fontWeight: 700, textAlign: "center",
                                      background: scaduto ? "#ffebee" : (futuro ? "#fff3e0" : "#e8f5e9"),
                                      color: scaduto ? "#b71c1c" : (futuro ? "#bf360c" : "#1b5e20"),
                                      border: `1px solid ${scaduto ? "#ef9a9a" : (futuro ? "#ffcc80" : "#a5d6a7")}`,
                                    }}>
                                    {validity.label}
                                  </div>
                                )}
                                <div style={{ display: "flex", gap: 6 }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 9, fontWeight: 700, color: "#0d47a1", textAlign: "left" }}>📅 Valido dal:</div>
                                    <input type="date"
                                      data-testid={`moduli-vdal-${it.Cod}-${c.id}`}
                                      value={getValidoDal(it.Cod, c.id, valDal)}
                                      onChange={(e) => setValidoDal(it.Cod, c.id, e.target.value)}
                                      style={{ width: "100%", padding: "4px 6px", fontSize: 11, border: "1px solid #bdbdbd", borderRadius: 2, boxSizing: "border-box" }} />
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 9, fontWeight: 700, color: "#0d47a1", textAlign: "left" }}>📅 Valido fino al:</div>
                                    <input type="date"
                                      data-testid={`moduli-vfino-${it.Cod}-${c.id}`}
                                      value={getValidoFino(it.Cod, c.id, valFino)}
                                      onChange={(e) => setValidoFino(it.Cod, c.id, e.target.value)}
                                      style={{ width: "100%", padding: "4px 6px", fontSize: 11, border: "1px solid #bdbdbd", borderRadius: 2, boxSizing: "border-box" }} />
                                  </div>
                                </div>
                                <div>
                                  <div style={{ fontSize: 9, fontWeight: 700, color: "#0d47a1", textAlign: "left" }}>📝 Nota:</div>
                                  <textarea
                                    data-testid={`moduli-nota-${it.Cod}-${c.id}`}
                                    value={getNotaDraft(it.Cod, c.id, doc?.protocollo_nota || "")}
                                    onChange={(e) => setNotaDraft(it.Cod, c.id, e.target.value)}
                                    placeholder="Aggiungi note (es. dosaggio, validità, contatti…)"
                                    rows={2}
                                    style={{ width: "100%", padding: "4px 6px", fontSize: 11, border: "1px solid #bdbdbd", borderRadius: 2, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }}
                                  />
                                </div>
                                <button onClick={() => hasProtocollo ? onSalvaNota(it.Cod, c.id) : onRegistraProtocollo(it.Cod, c.id)}
                                  data-testid={`moduli-registra-${it.Cod}-${c.id}`}
                                  style={btnStyleAction("#2e7d32")}>
                                  {hasProtocollo ? "💾 Salva modifiche" : "✓ Registra e assegna protocollo"}
                                </button>
                                {hasProtocollo && annoCorrente && (
                                  <button onClick={() => onDuplicaAnnoSuccessivo(it.Cod, c.id)}
                                    data-testid={`moduli-duplica-${it.Cod}-${c.id}`}
                                    style={btnStyleAction("#1565c0")}
                                    title={`Crea lo stesso protocollo ${doc.numero_protocollo} anche per l'A.S. ${annoCorrente + 1}/${(annoCorrente + 2) % 100}`}>
                                    📋 Duplica nell'A.S. {annoCorrente + 1}/{((annoCorrente + 2) % 100).toString().padStart(2, "0")}
                                  </button>
                                )}
                                <button onClick={() => onPulisciCampo(it.Cod, c.id)}
                                  data-testid={`moduli-pulisci-${it.Cod}-${c.id}`}
                                  style={btnStyleAction("#c62828")}>🧹 Cancella la registrazione · Pulisci campo</button>
                                <button onClick={() => setActiveCell(null)}
                                  style={{ ...btnStyleAction("#888"), marginTop: 2 }}>✕ Chiudi</button>
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                      );
                    })}
            </tbody>
          </table>
        </div>
      </div>

      <input ref={fileInputRef} type="file" style={{ display: "none" }} onChange={onFileChosen}
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" />

      {showStampaModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
        }} onClick={() => setShowStampaModal(false)} data-testid="stampa-modal">
          <div style={{
            background: "white", borderRadius: 6, padding: 20, minWidth: 380, maxWidth: 480,
            boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 12px 0", color: "#0d47a1", fontSize: 16 }}>🖨 Stampa elenco modulo</h3>
            <div style={{ fontSize: 12, color: "#555", marginBottom: 10, lineHeight: 1.4 }}>
              Verrà stampato in PDF (A4 orizzontale) l'elenco di tutti gli alunni che hanno il <b>protocollo
              attivo</b> per il modulo selezionato, ordinato per <b>settore</b> e <b>classe</b>, con data di
              attivazione e nota.
            </div>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#333", display: "block", marginBottom: 4 }}>
              Modulo:
            </label>
            <select value={stampaCatId} onChange={(e) => setStampaCatId(e.target.value)}
              data-testid="stampa-cat-select"
              style={{ width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #999", borderRadius: 3, marginBottom: 14 }}>
              <option value="">— Seleziona un modulo —</option>
              {categorieSport.map(c => (
                <option key={c.id} value={c.id}>{c.codice ? `[${c.codice}] ` : ""}{c.descrizione}</option>
              ))}
            </select>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setShowStampaModal(false)}
                style={{ padding: "7px 14px", background: "#9e9e9e", color: "white", border: "none", borderRadius: 3, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                Annulla
              </button>
              <button onClick={onStampaCategoria}
                data-testid="stampa-conferma-btn"
                disabled={!stampaCatId}
                style={{ padding: "7px 14px", background: stampaCatId ? "#1565c0" : "#bdbdbd", color: "white", border: "none", borderRadius: 3, fontSize: 12, fontWeight: 700, cursor: stampaCatId ? "pointer" : "not-allowed" }}>
                🖨 Genera PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const btnStyleAction = (bg) => ({
  padding: "4px 8px", background: bg, color: "white", border: "none", borderRadius: 2,
  fontSize: 10, fontWeight: 700, cursor: "pointer", textAlign: "left",
});
